import orderBy from 'lodash/orderBy';
import { computed } from 'vue';
import ApiClient from '@/ApiClient';
import { useStateComposable } from '@/state/composables';

const { computedState, state, reset } = useStateComposable({
  invalidBankDetails: {},
  missedSalaries: {},
  neverPaid: {},
  failedSalaries: {},
  returnedSalaries: [],
  error: false,
  loading: false,
});

const invalidBankDetailsEmployees = computed(() => getSortedReportEmployees(state.invalidBankDetails));
const missedSalaryEmployees = computed(() => getSortedReportEmployees(state.missedSalaries));
const neverPaidEmployees = computed(() => getSortedReportEmployees(state.neverPaid));
const failedSalaryEmployees = computed(() => getSortedReportEmployees(state.failedSalaries));
const returnedSalaryEmployees = computed(() => getSortedReportEmployees(state.returnedSalaries));

const hasInvalidBankDetailsEmployees = computed(() => invalidBankDetailsEmployees.value.length > 0);
const hasMissedSalaryEmployees = computed(() => missedSalaryEmployees.value.length > 0);
const hasNeverPaidEmployees = computed(() => neverPaidEmployees.value.length > 0);
const hasFailedSalaryEmployees = computed(() => failedSalaryEmployees.value.length > 0);
const hasReturnedSalaryEmployees = computed(() => returnedSalaryEmployees.value.length > 0);

const getEmployeesReport = async (
  startDate,
  endDate,
  filter = {
    invalidBankDetails: true,
    missedSalaries: true,
    neverPaid: true,
    failedSalaries: true,
    returnedSalaries: true,
  },
) => {
  reset();

  state.loading = true;

  try {
    const [invalidBankDetails, missedSalaries, neverPaid, failedSalaries, returnedSalaries] = await Promise.all([
      filter.invalidBankDetails ? getInvalidBankDetails() : {},
      filter.missedSalaries ? getMissedSalaries(startDate, endDate) : {},
      filter.neverPaid ? getNeverPaid() : {},
      filter.failedSalaries ? getFailedSalaries(startDate, endDate) : {},
      filter.returnedSalaries ? getReturnedSalaries(startDate, endDate) : [],
    ]);

    state.invalidBankDetails = invalidBankDetails;
    state.missedSalaries = missedSalaries;
    state.neverPaid = neverPaid;
    state.failedSalaries = failedSalaries;
    state.returnedSalaries = returnedSalaries;
  } catch (error) {
    console.error(error);
    state.error = true;
  }

  state.loading = false;
};

const getInvalidBankDetails = async () => (await ApiClient.getInvalidBankDetails())?.data || {};

const getMissedSalaries = async (startDate, endDate) =>
  (await ApiClient.getMissedSalaries({ starts_on: startDate, ends_on: endDate }))?.data || {};

const getNeverPaid = async () => (await ApiClient.smeGetEmployeesWithoutPayments())?.data?.employees || {};

const getFailedSalaries = async (startDate, endDate) =>
  (await ApiClient.getFailedSalaries({ starts_on: startDate, ends_on: endDate }))?.data || {};

const getReturnedSalaries = async (startDate, endDate) =>
  (await ApiClient.getReturnedSalaries({ starts_on: startDate, ends_on: endDate }))?.data || [];

const isInvalidBankDetailsEmployee = employeeId =>
  invalidBankDetailsEmployees.value.find(employee => employee.employee_id === employeeId);
const isMissedSalaryEmployee = employeeId =>
  missedSalaryEmployees.value.find(employee => employee.employee_id === employeeId);
const isNeverPaidEmployee = employeeId =>
  neverPaidEmployees.value.find(employee => employee.employee_id === employeeId);
const isFailedSalaryEmployee = employeeId =>
  failedSalaryEmployees.value.find(employee => employee.employee_id === employeeId);
const isReturnedSalaryEmployee = employeeId =>
  returnedSalaryEmployees.value.find(employee => employee.employee_id === employeeId);

const getSortedReportEmployees = reportEmployees => orderBy(Object.values(reportEmployees).flat(), 'full_name', 'asc');

export default () => {
  return {
    ...computedState,
    invalidBankDetailsEmployees,
    missedSalaryEmployees,
    neverPaidEmployees,
    failedSalaryEmployees,
    returnedSalaryEmployees,
    hasInvalidBankDetailsEmployees,
    hasMissedSalaryEmployees,
    hasNeverPaidEmployees,
    hasFailedSalaryEmployees,
    hasReturnedSalaryEmployees,
    isInvalidBankDetailsEmployee,
    isMissedSalaryEmployee,
    isNeverPaidEmployee,
    isFailedSalaryEmployee,
    isReturnedSalaryEmployee,
    getEmployeesReport,
  };
};
